import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ShieldAlert, RotateCw, ExternalLink } from "lucide-react";

export const Route = createFileRoute("/admin/rls-audit")({
  component: Page,
  head: () => ({
    meta: [
      { title: "RLS audit — photo_credits & subscriptions" },
      {
        name: "description",
        content:
          "Přehled zablokovaných pokusů o zápis do citlivých tabulek.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type Row = {
  id: string;
  occurred_at: string;
  source: "client" | "pg_log" | "server";
  actor_id: string | null;
  actor_email: string | null;
  table_name: string;
  operation: "insert" | "update" | "delete" | "select";
  error_code: string | null;
  error_message: string | null;
  policy_hint: string | null;
  request_path: string | null;
  user_agent: string | null;
  payload: unknown;
};

const fmt = (s: string) => new Date(s).toLocaleString("cs-CZ");

const OP_VARIANT: Record<Row["operation"], "default" | "secondary" | "destructive" | "outline"> = {
  insert: "default",
  update: "secondary",
  delete: "destructive",
  select: "outline",
};

function Page() {
  const [table, setTable] = useState<"all" | "photo_credits" | "subscriptions">(
    "all",
  );
  const [op, setOp] = useState<"all" | Row["operation"]>("all");
  const [sinceDays, setSinceDays] = useState(7);
  const [limit, setLimit] = useState(200);

  const filters = useMemo(
    () => ({ table, op, sinceDays, limit }),
    [table, op, sinceDays, limit],
  );

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: ["admin", "rls-audit", filters],
    queryFn: async (): Promise<Row[]> => {
      const tables =
        filters.table === "all"
          ? ["photo_credits", "subscriptions"]
          : [filters.table];
      const since = new Date(
        Date.now() - filters.sinceDays * 24 * 60 * 60 * 1000,
      ).toISOString();

      const rpc = supabase.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: Row[] | null; error: { message: string } | null }>;

      const { data, error } = await rpc("list_write_denied_audit", {
        _tables: tables,
        _since: since,
        _limit: filters.limit,
      });
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as Row[];
      return filters.op === "all"
        ? rows
        : rows.filter((r) => r.operation === filters.op);
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <ShieldAlert className="h-5 w-5 text-destructive" />
        <h1 className="text-2xl font-semibold">
          RLS audit — zablokované zápisy
        </h1>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Zdroje záznamů</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <Badge variant="default" className="mr-1.5">
              client
            </Badge>
            Pokusy z naší aplikace — logované klientem přes wrapper{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">
              auditWrite()
            </code>{" "}
            po zachycení chyby 42501 / RLS.
          </p>
          <p>
            <Badge variant="outline" className="mr-1.5">
              pg_log
            </Badge>
            Vyhrazeno pro externí pokusy (přímé REST volání zvenčí). Ty
            zachytává jen Postgres log v Lovable Cloud → Logy — otevřete v
            samostatné záložce a filtrujte na{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">
              row-level security
            </code>{" "}
            nebo{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">
              permission denied
            </code>
            .
          </p>
          <div>
            <a
              href="/cloud/logs"
              className="inline-flex items-center gap-1 text-sm text-primary underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Otevřít Cloud logy <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filtry</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <Label>Tabulka</Label>
            <Select
              value={table}
              onValueChange={(v) => setTable(v as typeof table)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všechny</SelectItem>
                <SelectItem value="photo_credits">photo_credits</SelectItem>
                <SelectItem value="subscriptions">subscriptions</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Operace</Label>
            <Select value={op} onValueChange={(v) => setOp(v as typeof op)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všechny</SelectItem>
                <SelectItem value="insert">insert</SelectItem>
                <SelectItem value="update">update</SelectItem>
                <SelectItem value="delete">delete</SelectItem>
                <SelectItem value="select">select</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="days">Poslední (dnů)</Label>
            <Input
              id="days"
              type="number"
              min={1}
              max={90}
              value={sinceDays}
              onChange={(e) =>
                setSinceDays(
                  Math.max(1, Math.min(90, Number(e.target.value) || 7)),
                )
              }
            />
          </div>
          <div>
            <Label htmlFor="limit">Limit</Label>
            <Input
              id="limit"
              type="number"
              min={1}
              max={1000}
              value={limit}
              onChange={(e) =>
                setLimit(
                  Math.max(1, Math.min(1000, Number(e.target.value) || 200)),
                )
              }
            />
          </div>
          <div className="flex items-end gap-2">
            <Button
              onClick={() => refetch()}
              disabled={isFetching}
              className="gap-1.5"
            >
              <RotateCw
                className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
              />
              Načíst
            </Button>
          </div>
          <div className="text-xs text-muted-foreground sm:col-span-2 lg:col-span-5">
            {data ? `${data.length} záznam(ů)` : ""}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {error ? (
            <div className="p-4 text-sm text-destructive">
              {(error as Error).message}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Čas</TableHead>
                  <TableHead>Zdroj</TableHead>
                  <TableHead>Tabulka</TableHead>
                  <TableHead>Op</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Chyba</TableHead>
                  <TableHead>Payload / hint</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data ?? []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs">
                      {fmt(r.occurred_at)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          r.source === "client" ? "default" : "outline"
                        }
                      >
                        {r.source}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {r.table_name}
                    </TableCell>
                    <TableCell>
                      <Badge variant={OP_VARIANT[r.operation]}>
                        {r.operation}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[180px] truncate text-xs">
                      {r.actor_email ?? (
                        <span className="font-mono text-[11px] text-muted-foreground">
                          {r.actor_id
                            ? r.actor_id.slice(0, 8) + "…"
                            : "anon"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[280px] text-xs text-destructive">
                      {r.error_code ? (
                        <span className="mr-1 font-mono">{r.error_code}</span>
                      ) : null}
                      {r.error_message}
                    </TableCell>
                    <TableCell className="max-w-[360px]">
                      {r.policy_hint ? (
                        <div className="mb-1 text-xs text-muted-foreground">
                          {r.policy_hint}
                        </div>
                      ) : null}
                      {r.payload !== null && r.payload !== undefined ? (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-muted-foreground">
                            payload
                          </summary>
                          <pre className="mt-1 overflow-auto rounded bg-muted p-2 text-[11px]">
                            {JSON.stringify(r.payload, null, 2)}
                          </pre>
                        </details>
                      ) : null}
                      {r.request_path ? (
                        <div className="mt-1 truncate text-[11px] text-muted-foreground">
                          {r.request_path}
                        </div>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
                {!isFetching && (data?.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="py-8 text-center text-sm text-muted-foreground"
                    >
                      Žádné zablokované zápisy v posledních {sinceDays} dnech.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
